/**
 * Admin authentication provider.
 *
 * Manages JWT-based auth state for the admin dashboard.
 * Supports two login methods:
 * - Email OTP: request code, verify code → JWT pair
 * - Google: exchange Google ID token → JWT pair
 *
 * Tokens are held in memory only (never localStorage).
 * Access token is refreshed automatically before expiry.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  type ReactNode,
} from 'react';
import type { AdminAuthContextValue, AdminAuthState, AdminTokens, AdminUser } from './types';
import * as api from '../api-client';
import { setTokenGetter } from '../api-client';

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

const INITIAL_STATE: AdminAuthState = {
  isInitialized: false,
  isAuthenticated: false,
  isLoading: false,
  user: null,
  error: null,
};

/** Refresh 60 seconds before expiry. */
const REFRESH_BUFFER_MS = 60_000;

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AdminAuthState>(INITIAL_STATE);
  const [tokens, setTokens] = useState<AdminTokens | null>(null);
  const bootstrapPromiseRef = useRef<Promise<api.TokenResponse | null> | null>(null);

  // ── Token refresh timer ─────────────────────────────────────────────

  useEffect(() => {
    if (!tokens) return;

    const msUntilRefresh = tokens.expiresAt - Date.now() - REFRESH_BUFFER_MS;
    if (msUntilRefresh <= 0) return; // already past — next request will refresh

    const timer = window.setTimeout(async () => {
      try {
        const res = await api.refreshTokens();
        setTokens({
          accessToken: res.access_token,
          expiresAt: res.expires_at,
        });
        setState({
          isInitialized: true,
          isAuthenticated: true,
          isLoading: false,
          user: {
            id: res.user.id,
            email: res.user.email,
            displayName: res.user.display_name,
          },
          error: null,
        });
      } catch {
        // Refresh failed — force re-login
        setTokens(null);
        setState({
          isInitialized: true,
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: 'Session expired. Please sign in again.',
        });
      }
    }, msUntilRefresh);

    return () => clearTimeout(timer);
  }, [tokens]);

  // ── Helpers ─────────────────────────────────────────────────────────

  const handleTokenResponse = useCallback((res: api.TokenResponse) => {
    setTokens({
      accessToken: res.access_token,
      expiresAt: res.expires_at,
    });
    const user: AdminUser = {
      id: res.user.id,
      email: res.user.email,
      displayName: res.user.display_name,
    };
    setState({
      isInitialized: true,
      isAuthenticated: true,
      isLoading: false,
      user,
      error: null,
    });
  }, []);

  // ── Actions ─────────────────────────────────────────────────────────

  const requestOtp = useCallback(async (email: string) => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      await api.requestOtp(email);
      setState((s) => ({ ...s, isLoading: false }));
    } catch (err) {
      const msg = err instanceof api.AdminApiError ? err.detail : 'Failed to send OTP';
      setState((s) => ({ ...s, isLoading: false, error: msg }));
      throw err;
    }
  }, []);

  const login = useCallback(
    async (email: string, otp: string) => {
      setState((s) => ({ ...s, isLoading: true, error: null }));
      try {
        const res = await api.verifyOtp(email, otp);
        handleTokenResponse(res);
      } catch (err) {
        const msg = err instanceof api.AdminApiError ? err.detail : 'Invalid OTP';
        setState((s) => ({ ...s, isLoading: false, error: msg }));
        throw err;
      }
    },
    [handleTokenResponse],
  );

  const loginWithGoogle = useCallback(
    async (idToken: string) => {
      setState((s) => ({ ...s, isLoading: true, error: null }));
      try {
        const res = await api.exchangeGoogleToken(idToken);
        handleTokenResponse(res);
      } catch (err) {
        const msg = err instanceof api.AdminApiError ? err.detail : 'Google sign-in failed';
        setState((s) => ({ ...s, isLoading: false, error: msg }));
        throw err;
      }
    },
    [handleTokenResponse],
  );

  const logout = useCallback(() => {
    return (async () => {
      try {
        await api.logout();
      } catch {
        // Best-effort logout; local state is still cleared below.
      }
      setTokens(null);
      setState({
        isInitialized: true,
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });
    })();
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!tokens) return null;

    // If token is near expiry, attempt refresh
    if (tokens.expiresAt - Date.now() < REFRESH_BUFFER_MS) {
      try {
        const res = await api.refreshTokens();
        const newTokens: AdminTokens = {
          accessToken: res.access_token,
          expiresAt: res.expires_at,
        };
        setTokens(newTokens);
        return newTokens.accessToken;
      } catch {
        setTokens(null);
        setState({
          isInitialized: true,
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: 'Session expired. Please sign in again.',
        });
        return null;
      }
    }

    return tokens.accessToken;
  }, [tokens]);

  // Wire token getter into the API client
  useLayoutEffect(() => {
    setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // Bootstrap session from HttpOnly refresh cookie on mount
  useEffect(() => {
    let active = true;

    if (!bootstrapPromiseRef.current) {
      bootstrapPromiseRef.current = (async () => {
        try {
          return await api.refreshTokens();
        } catch {
          return null;
        }
      })();
    }

    setState((s) => ({ ...s, isLoading: true }));
    void bootstrapPromiseRef.current.then((res) => {
      if (!active) {
        return;
      }
      if (res) {
        handleTokenResponse(res);
        return;
      }
      setTokens(null);
      setState({
        isInitialized: true,
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });
    });

    return () => {
      active = false;
    };
  }, [handleTokenResponse]);

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      ...state,
      login,
      loginWithGoogle,
      requestOtp,
      logout,
      getAccessToken,
    }),
    [state, login, loginWithGoogle, requestOtp, logout, getAccessToken],
  );

  return (
    <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>
  );
}

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return ctx;
}
